import clsx from "clsx";
import {
  CONTRADICTION_STATUS,
  DOCS_FORM_NAMES,
  FIELD_CREATION_TYPE,
  QUERY_PROGRESS_SUCCEED,
  RECARE_FILE_TYPE,
} from "core/consts";
import {
  Contradictions,
  DocsFormDataMapped,
  DocsFormName,
  DocsFormStatusResponse,
  EncryptedField,
  FieldCreationType,
  FormMetadata,
  RecareFileType,
  SourceFields,
} from "core/types";
import { Button, ButtonVariant, Tooltip } from "ds/ui";
import { useCopy } from "dsl/atoms/CopyButton/useCopy";
import {
  AlertTriangleIcon,
  CheckIcon,
  CopyIcon,
  FileSearch2Icon,
} from "lucide-react";
import { ReactNode } from "react";
import { convertEncryptedFieldOut } from "react-forms-state";
import { useWatch } from "react-hook-form";
import { useTranslations } from "translations";
import { SourceInformation } from "./useDocsForm";

export const FORM_NAME_TO_FILE_TYPE: Record<DocsFormName, RecareFileType> = {
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY]:
    RECARE_FILE_TYPE.DISCHARGE_LETTER_CARDIOLOGY,
  [DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT]: RECARE_FILE_TYPE.DISCHARGE_LETTER_ENT,
  [DOCS_FORM_NAMES.DRV_GENERAL]: RECARE_FILE_TYPE.DRV_GENERAL_FORM,
  [DOCS_FORM_NAMES.DRV_MEDICAL]: RECARE_FILE_TYPE.DRV_MEDICAL_FORM,
  [DOCS_FORM_NAMES.GKV_GENERAL]: RECARE_FILE_TYPE.GKV_GENERAL_FORM,
  [DOCS_FORM_NAMES.GKV_MEDICAL]: RECARE_FILE_TYPE.GKV_MEDICAL_FORM,
  [DOCS_FORM_NAMES.TRANSITIONAL_CARE]: RECARE_FILE_TYPE.TRANSITIONAL_CARE,
};

export type MigratedForms =
  | typeof DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT
  | typeof DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY;

const COPYABLE_FORMS: DocsFormName[] = [
  DOCS_FORM_NAMES.DISCHARGE_LETTER_CARDIOLOGY,
  DOCS_FORM_NAMES.DISCHARGE_LETTER_ENT,
];

const CopyButton = <T extends DocsFormName = DocsFormName>({
  fieldName,
}: {
  fieldName: keyof DocsFormDataMapped<T>;
}) => {
  const fieldValue = useWatch({
    name: fieldName as string,
  });
  const translations = useTranslations();
  const { copyMessage, copyStatus, copyToClipboard } = useCopy({
    onCopyMessage: "todo: translation",
  });

  const renderContent = () => (
    <Button
      aria-label={translations.chat.copyMessage}
      color="primary"
      id={`copy-button-${String(fieldName)}`}
      isDisabled={!fieldValue}
      isIconOnly
      startContent={
        copyStatus === QUERY_PROGRESS_SUCCEED ? <CheckIcon /> : <CopyIcon />
      }
      trackEventProps={{
        field_name: String(fieldName),
        id: `copy-button`,
      }}
      variant="ghost"
      onPress={() => copyToClipboard(fieldValue)}
    />
  );

  if (copyStatus === QUERY_PROGRESS_SUCCEED) {
    return (
      <Tooltip content={copyMessage} isOpen placement="bottom">
        {renderContent()}
      </Tooltip>
    );
  }

  return renderContent();
};

const SourceButton = ({
  fieldName,
  isEdited,
  isIconOnly,
  isSourceFieldsConflict,
  onPress,
}: {
  fieldName: string;
  isSourceFieldsConflict: boolean;
  isEdited: boolean;
  isIconOnly: boolean;
  onPress: () => void;
}) => {
  const translations = useTranslations();
  const buttonId = `${isEdited ? "reference" : "source"}-button`;
  const buttonLabel =
    translations.extractDocs.splitView.sourceInformation.buttonSource;
  const buttonText = isEdited
    ? translations.extractDocs.splitView.sourceInformation.buttonReference
    : translations.extractDocs.splitView.sourceInformation.buttonSource;

  const sharedProps = {
    "aria-label": buttonLabel,
    className: "whitespace-nowrap",
    color: isSourceFieldsConflict ? ("danger" as const) : ("primary" as const),
    endContent: isSourceFieldsConflict ? (
      <AlertTriangleIcon />
    ) : (
      <FileSearch2Icon />
    ),
    id: `${buttonId}-${fieldName}`,
    onPress,
    trackEventProps: {
      field_name: fieldName,
      id: buttonId,
    },
    variant: isEdited ? "light" : ("ghost" as ButtonVariant),
  };

  if (isIconOnly) {
    return <Button {...sharedProps} isIconOnly />;
  }

  return <Button {...sharedProps}>{buttonText}</Button>;
};

export const SourceWrapper = <T extends DocsFormName = DocsFormName>({
  children,
  fieldName,
  formName,
  hideSource,
  isIconOnly = false,
  sourceInformation,
}: {
  children: ReactNode;
  sourceInformation: SourceInformation<T>;
  fieldName: keyof DocsFormDataMapped<T>;
  isIconOnly?: boolean;
  hideSource?: boolean;
  formName: T;
}) => {
  if (!children) {
    return null;
  }

  if (hideSource) {
    return <div className="p-1">{children}</div>;
  }

  const isSelected = sourceInformation.isSourceSelected(fieldName);
  const sourceFields = sourceInformation.getSourceFields(fieldName);
  const isEdited = sourceFields.manually_edited;

  const canCopyResult = COPYABLE_FORMS.includes(formName);
  const sourceDocuments = sourceFields.source_documents ?? [];
  const hasSourceDocuments = sourceDocuments.length > 0;
  const contradictions = sourceInformation.getContradictionFields(fieldName);

  const isSourceFieldsConflict =
    contradictions?.status === CONTRADICTION_STATUS.CONFLICT &&
    (contradictions?.candidates?.length ?? 0) > 0;

  return (
    <div
      className={clsx("flex w-full gap-2 rounded-sm px-4 tablet:py-2", {
        "bg-blue-50": isSelected,
        "bg-white": !isSelected,
      })}
    >
      <div
        className={clsx("min-w-0", {
          "basis-[70%]": !isIconOnly,
          "basis-[85%]": isIconOnly,
        })}
      >
        {children}
      </div>
      <div className="flex min-w-0 flex-1 flex-row flex-wrap items-end justify-end gap-2">
        {canCopyResult && <CopyButton fieldName={fieldName} />}
        {hasSourceDocuments ? (
          <SourceButton
            fieldName={String(fieldName)}
            isEdited={isEdited}
            isIconOnly={isIconOnly}
            isSourceFieldsConflict={isSourceFieldsConflict}
            onPress={() => sourceInformation.handleSetSelectedSource(fieldName)}
          />
        ) : null}
      </div>
    </div>
  );
};

export function getFieldCreationType(
  inferredValue: unknown,
  value: unknown,
  sourceField: SourceFields | undefined,
): FieldCreationType {
  if (inferredValue != null && value != null) {
    return FIELD_CREATION_TYPE.INFERENCE;
  }
  if (sourceField?.creation_type) {
    return sourceField.creation_type;
  }
  return FIELD_CREATION_TYPE.USER;
}

export function getManuallyEdited(
  inferredValue: unknown,
  value: unknown,
  previousFieldValue: unknown,
  sourceField: SourceFields | undefined,
): boolean {
  if (inferredValue != null) {
    return value !== inferredValue;
  }
  if (previousFieldValue != null) {
    return previousFieldValue !== value || !!sourceField?.manually_edited;
  }
  return false;
}

export function buildFormMetadata<T extends DocsFormName>({
  docsFillFormResult,
  formData,
  previousFormData,
  sourceInformation,
}: {
  formData: DocsFormDataMapped<T>;
  sourceInformation: SourceInformation<T>;
  docsFillFormResult: DocsFormStatusResponse | null;
  previousFormData: DocsFormDataMapped<T> | null | undefined;
}): FormMetadata<T> {
  const sourceFields = Object.entries(formData).reduce(
    (acc, [key, value]) => {
      const inferredValue = docsFillFormResult?.form[key]?.value;
      const sourceField = sourceInformation.getSourceFields(
        key as keyof DocsFormDataMapped<T>,
      );
      const sourceDocuments = sourceField?.source_documents ?? [];
      const previousFieldValue =
        previousFormData?.[key as keyof DocsFormDataMapped<T>];

      return {
        ...acc,
        [key]: {
          creation_type: getFieldCreationType(
            inferredValue,
            value,
            sourceField,
          ),
          manually_edited: getManuallyEdited(
            inferredValue,
            value,
            previousFieldValue,
            sourceField,
          ),
          source_documents: sourceDocuments,
        },
      };
    },
    {} as FormMetadata<T>["source_fields"],
  );

  // Contradictions are stored at form level (encrypted), not per field in source_fields.
  const contradictions = Object.entries(formData).reduce<
    Record<string, Contradictions>
  >((acc, [key]) => {
    const fieldContradictions = sourceInformation.getContradictionFields(
      key as keyof DocsFormDataMapped<T>,
    );

    if (fieldContradictions == null) return acc;
    const value =
      typeof fieldContradictions === "object" &&
      "decrypted" in fieldContradictions
        ? (fieldContradictions as { decrypted?: string }).decrypted
        : fieldContradictions;
    const parsed: Contradictions | null =
      typeof value === "object" && value !== null && "status" in value
        ? (value as unknown as Contradictions)
        : typeof value === "string"
        ? (() => {
            try {
              return JSON.parse(value) as Contradictions;
            } catch {
              return null;
            }
          })()
        : null;
    if (parsed?.status != null) acc[key] = parsed;
    return acc;
  }, {});

  return {
    contradictions: (convertEncryptedFieldOut(JSON.stringify(contradictions)) ??
      null) as EncryptedField | null,
    source_fields: sourceFields,
  };
}
